import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { ApiError, receptionApi, type AppCall, type ConsolePerson, type ConsoleView, type OfferView, type TransferView } from '../../api/client';
import { Avatar } from '../../components/messages/Avatar';
import { Button, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { answer, callsNow, cancelTransfer, clearRecall, clockText, completeTransfer, consult, dial, hangup, hold, prettyNumber, switchTo, transfer, transferWithRecall, useCalls, useCallClock } from '../../lib/calls';
import { clockTime, plural } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import { useMessagesLive } from '../../lib/messages';

/**
 * The receptionist's console (1.13.0, Teampace 2713; docs/receptionist.md): their calls on the left,
 * the company's people in the middle, everything else a call can go to on the right.
 *
 * Sending a call to a person (Joey, Nov 3 2026: the four zones in a tile were too small, dropping
 * beside them sent the call blind, and he'd rather click): Transfer on the call, then click the
 * person, and a dialog offers four large choices that say what happens, Talk first by default
 * (never Send now). Or drag the call onto a person: the whole tile is the target, and letting go
 * opens the same dialog. (Four drop zones over the tile were tried on Nov 3: a neighbour's opened
 * as the pointer passed and covered the person aimed for.) Groups, park, mailboxes and outside
 * numbers have one meaning, so a click or drop sends the call there. Without a mouse: Transfer,
 * T, type a name, Enter (or 1 to 4). Space answers the next call.
 */

type Zone = 'blind' | 'warm' | 'ask' | 'voicemail';
/** The ways to a person, in the order they're offered (and numbered 1 to 4). */
const ZONES: { zone: Zone; label: string; icon: IconName }[] = [
  { zone: 'warm', label: 'Talk first', icon: 'phone' },
  { zone: 'blind', label: 'Send now', icon: 'arrowOut' },
  { zone: 'ask', label: 'Ask by message', icon: 'chat' },
  { zone: 'voicemail', label: 'Voicemail', icon: 'voicemail' }
];
/** One way to a person for this call: what the choice says, and whether it can be used. */
interface ZoneChoice {
  zone: Zone;
  /** "Talk to Marcus first" */
  title: string;
  /** What happens, or why not, for the dialog. */
  long: string;
  off: boolean;
}
const CALL_TYPE = 'text/x-stitch-call';

/** A second's tick, for clocks. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

const since = (iso: string | undefined, now: number) => (iso ? clockText(Math.max(0, Math.round((now - Date.parse(iso)) / 1000))) : '');
const callerOf = (c: Pick<AppCall, 'name' | 'number'>) => c.name || prettyNumber(c.number) || 'Unknown caller';
const firstName = (name: string) => name.split(/\s+/)[0] || name;

/** The console's data: loaded every few seconds, lines and asks changed live. */
function useConsole(domain: string) {
  const [view, setView] = useState<ConsoleView>();
  const [error, setError] = useState<Error | null>(null);
  /** Asks by id, as the latest word on each says (the server's, then live changes). */
  const [offers, setOffers] = useState<Record<string, OfferView>>({});
  const load = useCallback(async () => {
    try {
      const v = await receptionApi.console(domain);
      setView(v);
      setError(null);
      setOffers((o) => {
        const next = { ...o };
        for (const a of v.asks) next[a.id] = a;
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [domain]);
  useEffect(() => {
    let live = true;
    let timer: number | undefined;
    const tick = async () => {
      await load();
      // Lines and asks change live in between; this catches groups, park and the rest.
      if (live) timer = window.setTimeout(() => void tick(), document.hidden ? 30_000 : 8_000);
    };
    void tick();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [load]);
  useMessagesLive((e) => {
    if (e.type === 'reset' || (e.type === 'changed' && e.tenant === domain.toLowerCase())) void load();
    if (e.type === 'offer' && e.offer) {
      const o = e.offer;
      setOffers((x) => ({ ...x, [o.id]: o }));
      if (o.state === 'accepted' || o.state === 'voicemail') void load();
    }
    if (e.type === 'line' && e.tenant === domain.toLowerCase() && e.extension && e.state) {
      const { extension, state, dnd } = e;
      setView((v) =>
        v && {
          ...v,
          people: v.people.map((p) =>
            p.extension === extension ? { ...p, state: state as ConsolePerson['state'], ...(dnd !== undefined ? { dnd } : {}), ...(state === 'idle' ? { since: undefined, with: undefined } : {}) } : p
          )
        }
      );
    }
  });
  return { view, error, load, offers, setOffers };
}

/** "Asked Priya · 0:12 left", "Priya: Give me 2 minutes", "No answer from Priya"... */
function askLine(o: OfferView, now: number): string {
  const who = firstName(o.to.name);
  const left = Math.max(0, Math.round((Date.parse(o.expiresAt) - now) / 1000));
  switch (o.state) {
    case 'sent':
      return `Asked ${who} · ${clockText(left)} left`;
    case 'seen':
      return `${who} has seen it · ${clockText(left)} left`;
    case 'replied':
      return `${who}: Give me 2 minutes · ${clockText(left)} left`;
    case 'declined':
      return `${who}: Not now`;
    case 'expired':
      return `No answer from ${who}. The caller is still holding.`;
    case 'accepted':
      return `${who} took it`;
    case 'voicemail':
      return `Sent to ${who}’s voicemail`;
    case 'caller_left':
      return 'The caller hung up';
    case 'withdrawn':
      return 'You took it back';
    default:
      return 'The call couldn’t be sent on';
  }
}

function personState(p: ConsolePerson, now: number): string {
  if (p.dnd) return 'Do not disturb';
  switch (p.state) {
    case 'talking':
      return `On a call${p.since ? ` · ${since(p.since, now)}` : ''}${p.with ? ` with ${p.with.name || prettyNumber(p.with.number)}` : ''}`;
    case 'ringing':
      return 'Ringing';
    case 'offline':
      return 'Offline';
    case 'idle':
      return 'Available';
    default:
      return '';
  }
}

export function ConsolePage() {
  const { tenant: domain = '' } = useParams();
  const calls = useCalls();
  const { view, error, load, offers, setOffers } = useConsole(domain);
  const now = useNow();
  const [selected, setSelected] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'available' | 'stitch'>('all');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn' | 'crit'; text: string } | null>(null);
  const [asking, setAsking] = useState<{ call: AppCall; person: ConsolePerson } | null>(null);
  /** The call and person chosen, while the receptionist picks how (the dialog). */
  const [choice, setChoice] = useState<{ callId: string; person: ConsolePerson } | null>(null);
  const [busy, setBusy] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  /** When each call went on hold, and when each started ringing, on this screen's clock. */
  const heldAt = useRef(new Map<string, number>());
  const rangAt = useRef(new Map<string, number>());

  const mine = calls.calls.filter((c) => c.tenant === domain.toLowerCase() && c.state !== 'ended' && !c.left);
  const ringing = mine.filter((c) => c.state === 'ringing' && c.direction === 'inbound');
  // Less the calls ringing someone for a blind transfer that comes back: the caller's card says so.
  const onCalls = mine.filter((c) => !(c.state === 'ringing' && c.direction === 'inbound') && !calls.recallConsults[c.id]);
  for (const c of mine) {
    if (c.held && !heldAt.current.has(c.id)) heldAt.current.set(c.id, Date.now());
    if (!c.held) heldAt.current.delete(c.id);
    if (c.state === 'ringing' && !rangAt.current.has(c.id)) rangAt.current.set(c.id, Date.now());
  }
  const moving = dragging ?? selected;
  const movingCall = moving ? mine.find((c) => c.id === moving) ?? null : null;
  useEffect(() => {
    if (selected && !mine.some((c) => c.id === selected)) setSelected(null);
    // The caller hung up while the receptionist was choosing: nothing left to send.
    if (choice && !mine.some((c) => c.id === choice.callId)) setChoice(null);
  }, [mine, selected, choice]);

  /** The latest ask on each call. */
  const askOf = useMemo(() => {
    const out = new Map<string, OfferView>();
    for (const o of Object.values(offers)) {
      const cur = out.get(o.callId);
      if (!cur || cur.createdAt < o.createdAt) out.set(o.callId, o);
    }
    return out;
  }, [offers]);

  const people = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (view?.people ?? []).filter((p) => {
      if (q && !p.name.toLowerCase().includes(q) && !p.extension.includes(q)) return false;
      if (filter === 'available') return p.state === 'idle' && !p.dnd;
      if (filter === 'stitch') return p.stitchOpen;
      return true;
    });
  }, [view, search, filter]);

  const say = (tone: 'ok' | 'warn' | 'crit', text: string) => {
    setNotice({ tone, text });
    window.setTimeout(() => setNotice((n) => (n?.text === text ? null : n)), 6000);
  };
  const failed = (err: unknown) => say('crit', err instanceof Error ? err.message : String(err));
  const done = () => {
    setSelected(null);
    setDragging(null);
    setOver(null);
  };

  /** Send a call to a person, one of the four ways. */
  async function toPerson(callId: string, p: ConsolePerson, zone: Zone) {
    const call = mine.find((c) => c.id === callId);
    if (!call) return;
    done();
    const ringingFor = calls.recall[callId];
    if (ringingFor && ringingFor.until !== null) return say('warn', `Still ringing ${ringingFor.name} for this caller. Wait, or choose Resume to take them back first.`);
    if (ringingFor) void clearRecall(callId);
    if (zone === 'ask') {
      if (!p.userId) return say('warn', `${p.name} isn’t in Stitch, so they can’t be asked by message. Try Blind or Warm.`);
      if (call.state !== 'active') return say('warn', 'Answer the call first, then ask someone to take it.');
      setAsking({ call, person: p });
      return;
    }
    if (zone === 'warm') {
      if (call.state !== 'active') return say('warn', 'Answer the call first, then talk to them before you transfer it.');
      const ok = await consult(callId, p.extension);
      if (ok) void receptionApi.warm(domain, callId, p.extension, p.name).catch(() => undefined);
      return;
    }
    if (zone === 'voicemail' && !view?.settings.voicemailPrefix) return say('warn', 'To voicemail isn’t set up yet: an admin sets the mailbox prefix in Settings → Reception.');
    // A blind transfer that comes back if nobody answers (2714), when the company turned it on: for an
    // answered call, to someone whose phone can ring (do not disturb and offline go to voicemail anyway).
    const recall = view?.settings.recallSeconds ?? 0;
    if (zone === 'blind' && recall > 0 && call.state === 'active' && !p.dnd && p.state !== 'offline') {
      let logged: string | null = null;
      let over: 'answered' | 'came back' | null = null;
      const finish = (how: 'answered' | 'came back') => {
        over = how;
        if (logged) void receptionApi.transferOutcome(domain, logged, how).catch(() => undefined);
        void load();
      };
      if (await transferWithRecall(callId, p.extension, recall, p.name, finish)) {
        const { transfer: t } = await receptionApi.warm(domain, callId, p.extension, p.name, true).catch(() => ({ transfer: null }));
        logged = t?.id ?? null;
        if (logged && over) void receptionApi.transferOutcome(domain, logged, over).catch(() => undefined);
        void load();
      }
      return;
    }
    const ok = await transfer(callId, p.extension, (t, id, to) => receptionApi.transfer(t, id, zone === 'voicemail' ? 'voicemail' : 'blind', to, p.name));
    if (ok) {
      say('ok', zone === 'voicemail' ? `${callerOf(call)} went to ${p.name}’s voicemail.` : `${callerOf(call)} went to ${p.name}.`);
      void load();
    }
  }

  /** Send a call to a group, queue, park slot, mailbox or outside number. */
  async function toPlace(callId: string, kind: 'group' | 'queue' | 'park' | 'blind' | 'outside', to: string, name: string) {
    const call = mine.find((c) => c.id === callId);
    if (!call) return;
    done();
    const ringingFor = calls.recall[callId];
    if (ringingFor && ringingFor.until !== null) return say('warn', `Still ringing ${ringingFor.name} for this caller. Wait, or choose Resume to take them back first.`);
    if (ringingFor) void clearRecall(callId);
    const ok = await transfer(callId, to, (t, id, n) => receptionApi.transfer(t, id, kind, n, name));
    if (ok) {
      say('ok', kind === 'park' ? `${callerOf(call)} is parked on ${name} (${to}).` : `${callerOf(call)} went to ${name}.`);
      void load();
    }
  }

  /**
   * A queue caller out of turn (2714): the phone system gives a code for that caller and this app
   * dials it, so the caller is answered first and then on your line to transfer like any other call.
   */
  async function answerWaiting(queue: string, c: { id: string; from?: string }) {
    setBusy(true);
    try {
      const { dial: code } = await receptionApi.answerQueueCaller(domain, c.id);
      if (await dial(code)) say('ok', `Answering ${c.from ? prettyNumber(c.from) : 'the caller'} from ${queue}.`);
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
      void load();
    }
  }

  async function sendAsk(note: string) {
    if (!asking) return;
    setBusy(true);
    try {
      const { offer } = await receptionApi.ask(domain, asking.call.id, asking.person.userId!, note);
      setOffers((x) => ({ ...x, [offer.id]: offer }));
      setAsking(null);
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  }

  async function backToCaller(c: AppCall) {
    const o = askOf.get(c.id);
    if (o && ['sent', 'seen', 'replied'].includes(o.state)) {
      try {
        const { offer } = await receptionApi.answer(domain, o.id, 'withdraw');
        setOffers((x) => ({ ...x, [offer.id]: offer }));
      } catch (err) {
        if (!(err instanceof ApiError && err.code === 'ASK_OVER')) failed(err);
      }
    }
    await clearRecall(c.id);
    // A call whose sound is elsewhere (it came back from a transfer, or you're on another): switch to it.
    if (callsNow().mine !== c.id) await switchTo(c.id);
    else if (c.held) await hold(c.id, false);
  }

  async function setBreak(on: boolean) {
    setBusy(true);
    try {
      await receptionApi.setBreak(domain, on);
      await load();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  }

  /** The four ways to a person for this call, each saying what happens or why it can't be used. */
  const choicesFor = (p: ConsolePerson, call: AppCall | null): ZoneChoice[] => {
    const first = firstName(p.name);
    const caller = call ? callerOf(call) : 'The caller';
    const active = call?.state === 'active';
    const offline = p.state === 'offline';
    const recall = view?.settings.recallSeconds ?? 0;
    return ZONES.map(({ zone }): ZoneChoice => {
      switch (zone) {
        case 'warm': {
          const title = `Talk to ${first} first`;
          if (offline) return { zone, title, long: `${first} is offline.`, off: true };
          if (!active) return { zone, title, long: 'Answer the call first, then talk to them before you hand it over.', off: true };
          return { zone, title, long: `${caller} holds while you talk to ${first}. Then hand the call over, or go back to ${caller}.`, off: false };
        }
        case 'blind': {
          const title = `Send to ${first} now`;
          if (p.dnd || offline) return { zone, title, long: `${first} is ${p.dnd ? 'on do not disturb' : 'offline'}, so ${caller} goes to their voicemail.`, off: false };
          if (recall > 0 && active) return { zone, title, long: `Rings ${first} now. If they don’t answer in ${recall} seconds, ${caller} comes back to you.`, off: false };
          return { zone, title, long: `Rings ${first} now, and you’re off the call.`, off: false };
        }
        case 'ask': {
          const title = `Ask ${first} by message`;
          if (!p.userId) return { zone, title, long: `${first} doesn’t sign in to Stitch, so they can’t be asked by message.`, off: true };
          if (!view?.settings.askOn) return { zone, title, long: 'Ask by message is turned off for your company (Settings → Reception).', off: true };
          if (!active) return { zone, title, long: 'Answer the call first, then ask someone to take it.', off: true };
          return { zone, title, long: `${caller} holds while ${first} answers in Stitch${p.dnd ? '. They’re on do not disturb, but they still see it' : ''}.`, off: false };
        }
        case 'voicemail': {
          const title = `${first}’s voicemail`;
          if (!view?.settings.voicemailPrefix) return { zone, title, long: 'Not set up yet: an admin sets the mailbox prefix in Settings → Reception.', off: true };
          return { zone, title, long: `${caller} goes straight to ${first}’s voicemail to leave a message.`, off: false };
        }
      }
    });
  };
  /** Use one way, or say why it can't be used. */
  const pick = (callId: string, p: ConsolePerson, c: ZoneChoice) => {
    if (c.off) return say('warn', c.long);
    setChoice(null);
    void toPerson(callId, p, c.zone);
  };

  // Space: answer the next call. T: find someone. Enter: choose how for the first one found; 1 to 4:
  // Talk first, Send now, Ask, Voicemail. Esc: stop moving.
  const keys = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keys.current = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
    if (asking || e.metaKey || e.ctrlKey || e.altKey) return;
    // The dialog is open: 1 to 4 choose (Esc closes it).
    if (choice) {
      if (/^[1-4]$/.test(e.key)) {
        const c = choicesFor(choice.person, mine.find((x) => x.id === choice.callId) ?? null)[Number(e.key) - 1];
        if (c) {
          e.preventDefault();
          pick(choice.callId, choice.person, c);
        }
      }
      return;
    }
    if (e.key === 'Escape') {
      done();
      return;
    }
    const inSearch = t === searchRef.current;
    // In the search box, Enter opens the choice for the first person found, and 1 to 4 send the call
    // once a name is typed (digits alone find an extension).
    if (selected && inSearch && e.key === 'Enter' && people[0]) {
      e.preventDefault();
      setChoice({ callId: selected, person: people[0] });
      return;
    }
    if (selected && (inSearch ? /[a-z]/i.test(search) : !typing) && /^[1-4]$/.test(e.key) && people[0]) {
      e.preventDefault();
      const c = choicesFor(people[0], mine.find((x) => x.id === selected) ?? null)[Number(e.key) - 1];
      if (c) pick(selected, people[0], c);
      return;
    }
    if (typing) return;
    if (e.key === ' ' && ringing[0]) {
      e.preventDefault();
      void answer(ringing[0].id);
    } else if (e.key === 't' || e.key === 'T') {
      e.preventDefault();
      searchRef.current?.focus();
    }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keys.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const dragProps = (c: AppCall) => ({
    draggable: true,
    onDragStart: (e: DragEvent) => {
      e.dataTransfer.setData(CALL_TYPE, c.id);
      e.dataTransfer.effectAllowed = 'move';
      setDragging(c.id);
    },
    onDragEnd: () => {
      setDragging(null);
      setOver(null);
    }
  });
  const dropProps = (key: string, act: (callId: string) => void) => ({
    onDragEnter: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes(CALL_TYPE)) return;
      e.preventDefault();
      if (over !== key) setOver(key);
    },
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes(CALL_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (over !== key) setOver(key);
    },
    onDragLeave: (e: DragEvent) => {
      // Moving onto something inside it (its words, or the choices over a person) isn't leaving it.
      // Some browsers leave relatedTarget empty on drag events: then where the pointer is says.
      const el = e.currentTarget as HTMLElement;
      const to = (e.relatedTarget as Node | null) ?? document.elementFromPoint(e.clientX, e.clientY);
      if (to && el.contains(to)) return;
      setOver((o) => (o === key ? null : o));
    },
    onDrop: (e: DragEvent) => {
      const id = e.dataTransfer.getData(CALL_TYPE);
      e.preventDefault();
      setDragging(null);
      setOver(null);
      if (id) act(id);
    }
  });

  if (!view) {
    return (
      <>
        <PageHeader title="Console" />
        <div className="content stack">{error ? <LoadError error={error} retry={() => void load()} /> : <Loading label="Loading the console" />}</div>
      </>
    );
  }
  const onBreak = Boolean(view.me.onBreak);
  const choosing = choice ? { call: mine.find((c) => c.id === choice.callId) ?? null, person: choice.person } : null;

  const placeRow = (key: string, icon: IconName, name: string, sub: string, act: (callId: string) => void, extra?: ReactNode) => (
    <li key={key} className={`console-place${moving ? ' moving' : ''}${over === key ? ' over' : ''}`} {...dropProps(key, act)}>
      <Icon name={icon} size={16} />
      <span className="cell-stack grow">
        <strong>{name}</strong>
        <small className="muted">{sub}</small>
      </span>
      {extra}
      {moving && (
        <Button size="small" onClick={() => act(moving)} aria-label={`Send the call to ${name}`}>
          Send here
        </Button>
      )}
    </li>
  );

  return (
    <>
      <PageHeader
        title="Console"
        chips={
          <Chip tone={onBreak ? 'warn' : 'ok'} small>
            {onBreak ? 'On break' : 'Available'}
          </Chip>
        }
      />
      <div className="content console">
      <div className="console-status" role="region" aria-label="Your status">
        <div className="segmented" role="group" aria-label="Status">
          <button type="button" className={onBreak ? '' : 'on'} aria-pressed={!onBreak} disabled={busy || !onBreak} onClick={() => void setBreak(false)}>
            Available
          </button>
          <button type="button" className={onBreak ? 'on' : ''} aria-pressed={onBreak} disabled={busy || onBreak} onClick={() => void setBreak(true)}>
            On break
          </button>
        </div>
        <span className="muted">{plural(view.me.transferredToday, 'call')} sent on today</span>
        <span className="grow" />
        <Button size="small" icon="phone" disabled={!ringing.length} onClick={() => ringing[0] && void answer(ringing[0].id)}>
          Answer next <kbd>Space</kbd>
        </Button>
      </div>
      {onBreak && <Notice tone="warn">You’re on break. Do not disturb is on and you’re signed out of your queues, so Operators and your queues won’t ring you.</Notice>}
      {!calls.available && calls.reason && <Notice tone="warn">{calls.reason}</Notice>}
      {/* Over the page, at the bottom: showing these never moves the columns under the pointer. */}
      <div className="console-float">
        {notice && (
          <div className={`console-toast ${notice.tone}`} role="status">
            {notice.text}
            <button type="button" className="link" onClick={() => setNotice(null)}>
              Dismiss
            </button>
          </div>
        )}
        {moving && movingCall && (
          <div className="console-moving" role="status">
            <Icon name="arrowOut" size={16} />
            <span className="grow">
              {dragging ? (
                <>
                  Drop <strong>{callerOf(movingCall)}</strong> on a person to choose how, or on a group, park or mailbox to send them there.
                </>
              ) : (
                <>
                  Where should <strong>{callerOf(movingCall)}</strong> go? Click a person to choose how, or <strong>Send here</strong> on a group. Or type a name, then Enter.
                </>
              )}
            </span>
            <Button size="small" variant="ghost" onClick={done}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="console-grid">
        {/* Calls */}
        <section className="console-col" aria-labelledby="console-calls">
          <h2 className="console-h" id="console-ringing">
            Ringing
          </h2>
          {ringing.length ? (
            <ul className="console-calls">
              {ringing.map((c) => (
                <li key={c.id} className="console-call ringing" {...dragProps(c)}>
                  <span className="cell-stack grow">
                    <strong>{callerOf(c)}</strong>
                    <small className="muted">
                      {c.name ? prettyNumber(c.number) : ''}
                      {c.forwardedFrom ? ` · for ${c.forwardedFrom.name || prettyNumber(c.forwardedFrom.number)}` : ''} · Ringing {clockText(Math.round((now - (rangAt.current.get(c.id) ?? now)) / 1000))}
                    </small>
                  </span>
                  <Button size="small" variant="primary" icon="phone" onClick={() => void answer(c.id)}>
                    Answer
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted console-empty">Nothing ringing.</p>
          )}
          <h2 className="console-h" id="console-calls">
            My calls
          </h2>
          {onCalls.length ? (
            <ul className="console-calls">
              {onCalls.map((c) => (
                <CallCard
                  key={c.id}
                  c={c}
                  now={now}
                  heldAt={heldAt.current.get(c.id) ?? null}
                  amber={view.settings.holdAmberSeconds}
                  red={view.settings.holdRedSeconds}
                  ask={askOf.get(c.id)}
                  selected={selected === c.id}
                  onSelect={() => {
                    const next = selected === c.id ? null : c.id;
                    setSelected(next);
                    // Choosing where it goes: the name search is ready to type in.
                    if (next) window.setTimeout(() => searchRef.current?.focus(), 0);
                  }}
                  onBack={() => void backToCaller(c)}
                  consultOf={c.consultFor ? mine.find((x) => x.id === c.consultFor) : undefined}
                  recall={calls.recall[c.id]}
                  drag={dragProps(c)}
                />
              ))}
            </ul>
          ) : (
            <p className="muted console-empty">No calls right now.</p>
          )}
        </section>

        {/* People */}
        <section className="console-col wide" aria-labelledby="console-people">
          <div className="console-people-head">
            <h2 className="console-h" id="console-people">
              People
            </h2>
            <input
              ref={searchRef}
              className="input console-search"
              type="search"
              placeholder="Find someone (T)"
              aria-label="Find someone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e: ReactKeyboardEvent) => {
                if (e.key === 'Escape') {
                  setSearch('');
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
          </div>
          <div className="segmented console-filters" role="group" aria-label="Show">
            {(
              [
                ['all', 'Everyone'],
                ['available', 'Available'],
                ['stitch', 'Stitch open']
              ] as const
            ).map(([k, label]) => (
              <button key={k} type="button" className={filter === k ? 'on' : ''} aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {label}
              </button>
            ))}
          </div>
          {people.length ? (
            <ul className="console-people">
              {people.map((p, i) => {
                const key = `p:${p.extension}`;
                const st = personState(p, now);
                const firstFound = Boolean(selected) && i === 0 && Boolean(search);
                const info = (
                  <>
                    {p.place ? (
                      <span className="console-place-icon" aria-hidden="true">
                        <Icon name="building" size={18} />
                      </span>
                    ) : (
                      <Avatar id={p.userId ?? `ext:${p.extension}`} name={p.name} />
                    )}
                    <span className="cell-stack grow">
                      <strong>
                        {p.name} <span className="muted">· {p.extension}</span>
                        {p.place && <span className="console-stitch">Shared phone</span>}
                      </strong>
                      <small className="console-state">{st || ' '}</small>
                    </span>
                    {p.stitchOpen && (
                      <span className="console-stitch" title="Stitch is open on their screen (active in the last 2 minutes)">
                        Stitch
                      </span>
                    )}
                  </>
                );
                return (
                  <li
                    key={p.extension}
                    className={`console-person${p.dnd ? ' dnd' : ` ${p.state}`}${over === key ? ' over' : ''}${firstFound ? ' first' : ''}${selected && !dragging ? ' choosable' : ''}`}
                    {...dropProps(key, (id) => setChoice({ callId: id, person: p }))}
                  >
                    {selected && !dragging ? (
                      // Choosing where a call goes: the whole tile is one button, and the dialog says how.
                      <button
                        type="button"
                        className="console-person-pick"
                        onClick={() => setChoice({ callId: selected, person: p })}
                        aria-label={`Send ${movingCall ? callerOf(movingCall) : 'the call'} to ${p.name}: choose how`}
                      >
                        {info}
                        <span className="console-pick-hint">{firstFound ? 'Enter' : 'Choose'}</span>
                      </button>
                    ) : (
                      <>
                        {info}
                        {p.state === 'ringing' && (
                          <Button size="small" onClick={() => void dial(`*87${p.extension}`)} aria-label={`Pick up the call ringing ${p.name}`}>
                            Pick up
                          </Button>
                        )}
                      </>
                    )}
                    {dragging && over === key && (
                      // Held over this person: the whole tile is the target, and letting go asks how (the dialog).
                      <span className="console-drop-hint" aria-hidden="true">
                        Let go to choose how
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <Empty title="No one matches.">Try another name or extension.</Empty>
          )}
        </section>

        {/* Everything else */}
        <section className="console-col" aria-label="Groups, park and more">
          <h2 className="console-h">Queues and groups</h2>
          <ul className="console-places">
            {view.groups.map((g) =>
              placeRow(
                `g:${g.extension}`,
                g.kind === 'callQueue' ? 'headset' : 'users',
                `${g.name} · ${g.extension}`,
                g.kind === 'callQueue' ? `Queue · ${g.waiting === undefined ? '' : g.waiting ? `${g.waiting} waiting` : 'none waiting'}` : g.operators ? 'Ring group · your receptionists' : 'Ring group',
                (id) => void toPlace(id, g.kind === 'callQueue' ? 'queue' : 'group', g.extension, g.name),
                g.callers?.length && !moving ? (
                  <ul className="console-waiting" aria-label={`Waiting in ${g.name}`}>
                    {g.callers.map((c) => (
                      <li key={c.id}>
                        <span className="grow">
                          {c.from ? prettyNumber(c.from) : 'Caller'} <small className="muted">· {clockText(c.waitingSeconds)}</small>
                        </span>
                        {c.canAnswer && (
                          <Button size="small" disabled={busy} onClick={() => void answerWaiting(g.name, c)} aria-label={`Answer ${c.from ? prettyNumber(c.from) : 'the caller'} waiting in ${g.name}`}>
                            Answer
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : undefined
              )
            )}
          </ul>
          {view.parks.length > 0 && (
            <>
              <h2 className="console-h">Park</h2>
              <ul className="console-places">
                {view.parks.map((p) => placeRow(`k:${p.extension}`, 'park', `${p.name} · ${p.extension}`, p.calls ? `${plural(p.calls, 'call')} parked` : 'Free', (id) => void toPlace(id, 'park', p.extension, p.name)))}
              </ul>
            </>
          )}
          {view.mailboxes.length > 0 && (
            <>
              <h2 className="console-h">Shared mailboxes</h2>
              <ul className="console-places">
                {view.mailboxes.map((b) => placeRow(`m:${b.extension}`, 'voicemail', `${b.name} · ${b.extension}`, 'Voicemail', (id) => void toPlace(id, 'blind', b.extension, b.name)))}
              </ul>
            </>
          )}
          {view.settings.outsideNumbers.length > 0 && (
            <>
              <h2 className="console-h">Outside numbers</h2>
              <ul className="console-places">
                {view.settings.outsideNumbers.map((o) => placeRow(`o:${o.number}`, 'arrowOut', o.name, prettyNumber(o.number), (id) => void toPlace(id, 'outside', o.number, o.name)))}
              </ul>
            </>
          )}
          <h2 className="console-h">Transfer log</h2>
          {view.transfers.length ? (
            <ul className="console-log">
              {view.transfers.slice(0, 30).map((t) => (
                <LogRow key={t.id} t={t} />
              ))}
            </ul>
          ) : (
            <p className="muted console-empty">Nothing sent on yet today.</p>
          )}
        </section>
      </div>

      {choosing && choice && (
        <ChooseDialog
          caller={choosing.call ? callerOf(choosing.call) : 'The caller'}
          person={choosing.person}
          state={[personState(choosing.person, now), choosing.person.stitchOpen ? 'Stitch open' : ''].filter(Boolean).join(' · ')}
          choices={choicesFor(choosing.person, choosing.call)}
          onPick={(c) => pick(choice.callId, choosing.person, c)}
          onClose={() => setChoice(null)}
        />
      )}
      {asking && (
        <AskDialog
          call={asking.call}
          person={asking.person}
          seconds={view.settings.askSeconds}
          notes={view.settings.quickNotes}
          busy={busy}
          onSend={(note) => void sendAsk(note)}
          onClose={() => setAsking(null)}
        />
      )}
      </div>
    </>
  );
}

const HOW: Record<TransferView['kind'], string> = {
  blind: 'blind',
  warm: 'warm',
  voicemail: 'to voicemail',
  message: 'by message',
  park: 'parked',
  queue: 'to the queue',
  group: 'to the group',
  outside: 'outside',
  pickup: 'picked up'
};

function LogRow({ t }: { t: TransferView }) {
  const who = t.callerName || prettyNumber(t.callerNumber) || 'A caller';
  return (
    <li>
      <time dateTime={t.at}>{clockTime(t.at)}</time>
      <span className="grow">
        {who} to {t.targetName || t.target} <span className="muted">· {HOW[t.kind]}{t.outcome && t.outcome !== 'sent' ? `, ${t.outcome}` : ''}</span>
      </span>
    </li>
  );
}

function CallCard({
  c,
  now,
  heldAt,
  amber,
  red,
  ask,
  selected,
  onSelect,
  onBack,
  consultOf,
  recall,
  drag
}: {
  c: AppCall;
  now: number;
  heldAt: number | null;
  amber: number;
  red: number;
  ask: OfferView | undefined;
  selected: boolean;
  onSelect: () => void;
  onBack: () => void;
  consultOf: AppCall | undefined;
  /** A blind transfer that comes back (2714): who is being rung and until when, or that nobody answered. */
  recall: { name: string; until: number | null } | undefined;
  drag: Record<string, unknown>;
}) {
  const secs = useCallClock(c.id);
  const held = heldAt !== null ? Math.round((now - heldAt) / 1000) : 0;
  const tone = c.held ? (held >= red ? 'red' : held >= amber ? 'amber' : '') : '';
  const live = ask && ['sent', 'seen', 'replied', 'expired', 'declined'].includes(ask.state);
  return (
    <li className={`console-call${c.held ? ' held' : ''}${tone ? ` ${tone}` : ''}${selected ? ' selected' : ''}`} {...(consultOf ? {} : drag)}>
      <div className="console-call-top">
        <button type="button" className="console-call-who" aria-pressed={selected} onClick={onSelect} disabled={Boolean(consultOf)} title={consultOf ? undefined : 'Choose where it goes, or drag it onto someone'}>
          {!consultOf && <Icon name="grip" size={14} />}
          <span className="cell-stack">
            <strong>{consultOf ? `Talking first: ${c.name || prettyNumber(c.number)}` : callerOf(c)}</strong>
            <small className="muted">
              {consultOf ? `for ${callerOf(consultOf)}, who is holding` : [c.name ? prettyNumber(c.number) : '', c.forwardedFrom ? `for ${c.forwardedFrom.name || prettyNumber(c.forwardedFrom.number)}` : ''].filter(Boolean).join(' · ')}
            </small>
          </span>
        </button>
        <span className={`console-clock${tone ? ` ${tone}` : ''}`} aria-label={c.held ? `On hold ${clockText(held)}` : `On the call ${clockText(secs)}`}>
          {c.held ? `Hold ${clockText(held)}` : c.state === 'dialing' ? 'Calling…' : clockText(secs)}
        </span>
      </div>
      {recall && (
        <div className={`console-ask ${recall.until === null ? 'expired' : 'sent'}`} role="status">
          <Icon name="arrowOut" size={14} />
          <span className="grow">
            {recall.until === null
              ? `Came back: ${recall.name} didn’t answer`
              : `Ringing ${recall.name} · comes back in ${clockText(Math.max(0, Math.round((recall.until - now) / 1000)))}`}
          </span>
        </div>
      )}
      {live && ask && (
        <div className={`console-ask ${ask.state}`} role="status">
          <Icon name="chat" size={14} />
          <span className="grow">{askLine(ask, now)}</span>
        </div>
      )}
      <div className="console-call-actions">
        {consultOf ? (
          <>
            <Button size="small" variant="primary" onClick={() => void completeTransfer(c)}>
              Complete transfer
            </Button>
            <Button size="small" onClick={() => void cancelTransfer(c)}>
              Back to caller
            </Button>
          </>
        ) : (
          <>
            {c.held ? (
              <Button size="small" variant="primary" onClick={onBack}>
                {live ? 'Back to caller' : 'Resume'}
              </Button>
            ) : (
              <Button size="small" disabled={c.state !== 'active'} onClick={() => void hold(c.id, true)}>
                Hold
              </Button>
            )}
            <Button size="small" onClick={onSelect}>
              {selected ? 'Cancel' : live ? 'Ask someone else' : 'Transfer'}
            </Button>
            <Button size="small" variant="ghost" icon="phoneOff" onClick={() => void hangup(c.id)}>
              Hang up
            </Button>
          </>
        )}
      </div>
    </li>
  );
}

/**
 * How to send a call to a person (Joey, Nov 3 2026): four large choices that say what happens, or
 * why one can't be used. Talk first has the focus when it can be used (then Ask, then Voicemail);
 * Send now never does, so Enter never sends a call blind. 1 to 4 choose too.
 */
function ChooseDialog({
  caller,
  person,
  state,
  choices,
  onPick,
  onClose
}: {
  caller: string;
  person: ConsolePerson;
  state: string;
  choices: ZoneChoice[];
  onPick: (c: ZoneChoice) => void;
  onClose: () => void;
}) {
  const focus = choices.find((c) => !c.off && c.zone !== 'blind');
  return (
    <Dialog open onClose={onClose} title={`Send ${caller} to ${person.name}`} subtitle={state}>
      <div className="console-choices" role="group" aria-label={`How to send ${caller} to ${person.name}`}>
        {choices.map((c, n) => {
          const z = ZONES.find((x) => x.zone === c.zone)!;
          return (
            <button key={c.zone} type="button" className="console-choice" disabled={c.off} autoFocus={focus?.zone === c.zone} onClick={() => onPick(c)}>
              <span className="console-choice-head">
                <Icon name={z.icon} size={18} />
                <strong className="grow">{c.title}</strong>
                <span className="console-choice-enter" aria-hidden="true">
                  Enter
                </span>
                <kbd aria-hidden="true">{n + 1}</kbd>
              </span>
              <span className="console-choice-what">{c.long}</span>
            </button>
          );
        })}
      </div>
    </Dialog>
  );
}

function AskDialog({
  call,
  person,
  seconds,
  notes,
  busy,
  onSend,
  onClose
}: {
  call: AppCall;
  person: ConsolePerson;
  seconds: number;
  notes: string[];
  busy: boolean;
  onSend: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const first = firstName(person.name);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Ask ${person.name}`}
      subtitle={person.stitchOpen ? 'Stitch is open on their screen' : 'Stitch isn’t open on their screen: they’ll get a notification'}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => onSend(note)}>
            Send and hold
          </Button>
        </>
      }
    >
      <p>
        <strong>{callerOf(call)}</strong>
        {call.name && call.number ? ` · ${prettyNumber(call.number)}` : ''}
      </p>
      {person.dnd && <Notice tone="warn">{first} is on do not disturb. They’ll still see the ask.</Notice>}
      <Field label={`Note for ${first}`} maxLength={200} value={note} autoFocus onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !busy && onSend(note)} />
      {notes.length > 0 && (
        <div className="console-notes" role="group" aria-label="Quick notes">
          {notes.map((n) => (
            <button key={n} type="button" className={`chip${note === n ? ' brand' : ''}`} aria-pressed={note === n} onClick={() => setNote(n)}>
              {n}
            </button>
          ))}
        </div>
      )}
      <p className="muted">
        {callerOf(call)} hears hold music while {first} decides. After {seconds} seconds, you choose what happens next.
      </p>
    </Dialog>
  );
}
